import authorizedMiddleware from "@/app/router/middlewares/auth";
import unauthorizedMiddleware from "@/app/router/middlewares/guest";
import NotFound from "@/components/shared/error-boundary/NotFound";
import AuthLayout from "@/layouts/AuthLayout";
import BlankLayout from "@/layouts/BlankLayout";
import DefaultLayout from "@/layouts/DefaultLayout";
import store from "@/store";

import { requireAccess, requireProductGroup, requireNotOnPrem, requireStaging } from "./guards";

const routes = [
  {
    path: "/",
    name: "common",
    component: AuthLayout,
    children: [
      {
        path: "",
        name: "common.home",
        redirect: {
          name: "dashboard.home",
        },
      },
      {
        path: "/home",
        name: "home",
        redirect: {
          name: "dashboard.home",
        },
      },
      {
        path: "/login",
        name: "common.login",
        component: () => import("@/pages/auth/LoginPage"),
        meta: { middleware: [unauthorizedMiddleware] },
      },
      {
        path: "/register",
        name: "common.register",
        component: () => import("@/pages/auth/RegisterPage"),
        beforeEnter: requireNotOnPrem("/login"),
        meta: { middleware: [unauthorizedMiddleware] },
      },
      {
        path: "/password-recovery",
        name: "common.password.recovery",
        component: () => import("@/pages/auth/PasswordRecoveryPage"),
        meta: { middleware: [unauthorizedMiddleware] },
      },
      {
        path: "/confirm-new-password",
        name: "common.create.new.password",
        component: () => import("@/pages/auth/ConfirmNewPassword"),
        meta: { middleware: [unauthorizedMiddleware] },
      },
      {
        path: "/supported-platforms-list",
        name: "common.supported.platforms.list",
        component: () => import("@/pages/deepfake/SupportedPlatformsList"),
      },
    ],
  },
  {
    path: "/",
    name: "dashboard",
    component: DefaultLayout,
    children: [
      {
        path: "/faq",
        name: "common.faq",
        component: () => import("@/pages/faq/FAQPage.vue"),
        meta: {
          middleware: [authorizedMiddleware],
          sidebar: { text: "FAQ", icon: "bi-question-circle", sortOrder: 8 },
        },
      },
      {
        path: "/analytics",
        name: "dashboard.analytics.list",
        component: () => {
          return import("@/pages/analytics/AnalyticsPage");
        },
        meta: {
          middleware: [authorizedMiddleware],
          sidebar: { i18nKey: "page.analytics.title", icon: "bi-graph-up", sortOrder: 4 },
        },
      },
      {
        path: "/team",
        name: "dashboard.team.management",
        component: () => import("@/pages/team/TeamManagement.vue"),
        meta: {
          middleware: [authorizedMiddleware],
          sidebar: { i18nKey: "page.team.title", icon: "bi-people", sortOrder: 5 },
        },
      },
      {
        path: "/developer",
        name: "dashboard.developer",
        component: () => import("@/pages/developer/DeveloperPage.vue"),
        meta: {
          middleware: [authorizedMiddleware],
          sidebar: {
            i18nKey: "page.developer.title",
            icon: "bi-code-slash",
            sortOrder: 7,
            visibleWhen: ["hasDetectionApiAccess", "notStaging", "isNotOnPrem"],
          },
        },
        beforeEnter: [
          requireNotOnPrem(),
          requireAccess(() => store.getters.hasDetectionApiAccess),
          (to, from, next) => {
            store.getters.isStaging ? next({ name: "dashboard.developer.api-keys" }) : next();
          },
        ],
      },
      {
        path: "/developer/api-keys",
        name: "dashboard.developer.api-keys",
        component: () => import("@/pages/developer/ApiKeysPage.vue"),
        beforeEnter: [
          requireAccess(() => store.getters.hasDetectionApiAccess),
          requireStaging({ name: "dashboard.developer" }),
        ],
        meta: {
          middleware: [authorizedMiddleware],
          sidebar: { parentId: "developer", i18nKey: "page.developer.apiKeys.title", icon: "bi-key", sortOrder: 1 },
        },
      },
      {
        path: "/developer/webhooks",
        name: "dashboard.developer.webhooks",
        component: () => import("@/pages/developer/WebhookManagementPage.vue"),
        beforeEnter: [
          requireAccess(() => store.getters.hasDetectionApiAccess),
          (to, from, next) => {
            if (!store.getters.isStaging) {
              next({ name: "dashboard.developer" });
            } else {
              next();
            }
          },
        ],
        meta: {
          middleware: [authorizedMiddleware],
          sidebar: {
            parentId: "developer",
            i18nKey: "page.webhooks.management.title",
            icon: "bi-lightning-charge",
            sortOrder: 2,
          },
        },
      },
      // api-docs is cloud-only: the `@scalar/api-reference` chunk and `public/openapi.json`
      // only need to ship in cloud builds. `import.meta.env.VITE_APP_IS_ONPREM` is inlined at
      // build time so rollup tree-shakes the ApiDocsPage.vue chunk out of the on-prem dist.
      ...(import.meta.env.VITE_APP_IS_ONPREM !== "on"
        ? [
            {
              path: "/developer/api-docs",
              name: "dashboard.developer.api-docs",
              component: () => import("@/pages/developer/ApiDocsPage.vue"),
              beforeEnter: [
                requireAccess(() => store.getters.hasDetectionApiAccess),
                requireStaging({ name: "dashboard.developer" }),
              ],
              meta: {
                middleware: [authorizedMiddleware],
                sidebar: {
                  parentId: "developer",
                  i18nKey: "page.developer.apiDocs.title",
                  icon: "bi-book",
                  sortOrder: 3,
                },
              },
            },
          ]
        : []),
      {
        path: "/developer/webhook-deliveries",
        name: "dashboard.developer.webhook-deliveries",
        redirect: (to) => ({
          name: "dashboard.developer.webhooks",
          query: {
            ...to.query,
            tab: "events",
          },
        }),
        beforeEnter: requireAccess(() => store.getters.hasDetectionApiAccess),
        meta: { middleware: [authorizedMiddleware] },
      },
      {
        path: "/integrations",
        name: "dashboard.integrations",
        component: () => {
          return import("@/pages/integrations/IntegrationsPage.vue");
        },
        beforeEnter: requireStaging(),
        meta: {
          middleware: [authorizedMiddleware],
          sidebar: { text: "Integrations", icon: "bi-plug", sortOrder: 6, visibleWhen: ["isStaging"] },
        },
      },
      {
        path: "/",
        name: "dashboard.home",
        component: () => import("@/pages/home/HomePage.vue"),
        meta: {
          middleware: [authorizedMiddleware],
          sidebar: { i18nKey: "page.home.title", icon: "bi-house", sortOrder: 0 },
        },
      },
      {
        path: "/deepfake-detection",
        name: "dashboard.deepfake.detection",
        component: () => {
          return import("@/pages/deepfake/DeepfakeList.vue");
        },
        beforeEnter: requireProductGroup("deepfake_detection"),
        meta: {
          middleware: [authorizedMiddleware],
          sidebar: {
            i18nKey: "name.service.deepfake",
            icon: "bi-person-bounding-box",
            sortOrder: 1,
            productGroup: "deepfake_detection",
          },
        },
      },
      {
        path: "/deepfake-detection/report/:id", // this is the legacy route, don't change 'report' to 'submission'
        name: "dashboard.deepfake.detection.submission.legacy",
        component: () => import("@/pages/deepfake/DeepfakeSubmissionLegacyRedirect.vue"),
        beforeEnter: requireProductGroup("deepfake_detection"),
        props: (route) => ({
          id: route.params.id,
        }),
        meta: { middleware: [authorizedMiddleware] },
      },
      {
        path: "/deepfake-detection/:id",
        name: "dashboard.deepfake.detection.detail",
        component: () => import("@/pages/deepfake/DeepfakeSubmission.vue"),
        beforeEnter: requireProductGroup("deepfake_detection"),
        props: (route) => ({
          id: route.params.id,
        }),
        meta: { middleware: [authorizedMiddleware] },
      },
      {
        path: "/document-verification",
        name: "dashboard.document.check",
        component: () => import("@/pages/document/DocumentVerificationList.vue"),
        beforeEnter: requireProductGroup("document_verification"),
        meta: {
          middleware: [authorizedMiddleware],
          sidebar: {
            i18nKey: "name.service.document",
            icon: "bi-file-earmark-text",
            sortOrder: 2,
            productGroup: "document_verification",
          },
        },
      },
      {
        path: "/document-verification/report/:id", // this is the legacy route, don't change 'report' to 'submission'
        name: "dashboard.document.check.submission.legacy",
        component: () => import("@/pages/document/DocumentVerificationLegacyRedirect.vue"),
        props: (route) => ({
          id: route.params.id,
        }),
        meta: { middleware: [authorizedMiddleware] },
        beforeEnter: requireProductGroup("document_verification"),
      },
      {
        path: "/document-verification/:id",
        name: "dashboard.document.check.detail",
        component: () => import("@/pages/document/DocumentVerificationSubmission.vue"),
        beforeEnter: requireProductGroup("document_verification"),
        props: (route) => ({
          id: route.params.id,
        }),
        meta: { middleware: [authorizedMiddleware] },
      },
      {
        path: "/archives",
        name: "dashboard.archives",
        redirect: { name: "dashboard.archives.video" },
        meta: { middleware: [authorizedMiddleware] },
      },
      {
        path: "/archives/video",
        name: "dashboard.archives.video",
        component: () => import("@/pages/archive/VideoExportsPage.vue"),
        meta: {
          middleware: [authorizedMiddleware],
          sidebar: {
            parentId: "archives",
            i18nKey: "page.archive.video.videoExports",
            icon: "bi-collection-play",
            sortOrder: 1,
          },
        },
      },
      {
        path: "/archives/csv",
        name: "dashboard.archives.csv",
        component: () => import("@/pages/archive/CSVExportsPage.vue"),
        meta: {
          middleware: [authorizedMiddleware],
          sidebar: {
            parentId: "archives",
            i18nKey: "page.archive.csv.csvExports",
            icon: "bi-filetype-csv",
            sortOrder: 2,
          },
        },
      },
      {
        path: "/:catchAll(.*)",
        name: "not-found",
        component: NotFound,
        meta: { middleware: [authorizedMiddleware] },
      },
    ],
  },
  {
    path: "/",
    name: "verification",
    component: BlankLayout,
    children: [
      {
        path: "/deepfake-detection/form",
        name: "deepfake.detection.form",
        component: () => import("@/pages/deepfake/DeepfakeForm"),
        beforeEnter: (to, from, next) => {
          if (!store.getters.isLicenseExpired && store.getters.hasAccessToProductGroup("deepfake_detection")) {
            next();
          } else {
            next("/");
          }
        },
        meta: { middleware: [authorizedMiddleware] },
      },
    ],
  },
];

export default routes;
